import { router } from "@inertiajs/react"
import { RefreshCw01 } from "@untitledui/icons"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Notice } from "@/components/ui/notice"
import { Panel } from "@/components/ui/panel"
import { formatRelative } from "@/lib/format"
import { adminEmailHealthPath } from "@/routes"

import type { Deliverability } from "../types"

import { CheckRow } from "./checks"

/**
 * SPF/DKIM/DMARC per sending domain, honouring the three-state: a check we
 * cannot run (no DKIM selector on record, say) is not a failing check. A
 * domain whose DNS lookup failed gets one attention line, not three unknown
 * rows implying record-level facts we don't have — and the re-check button
 * beside it is that condition's remedy.
 */
export function DomainHealth({
  deliverability,
}: {
  deliverability: Deliverability
}) {
  const [checking, setChecking] = useState(false)

  const recheck = () => {
    if (checking) return
    router.visit(adminEmailHealthPath(), {
      data: { refresh_dns: "1" },
      preserveScroll: true,
      onStart: () => setChecking(true),
      onFinish: () => setChecking(false),
    })
  }

  return (
    <Panel
      id="domains"
      title="Deliverability"
      description={`Live DNS, checked ${formatRelative(deliverability.checked_at)}. DMARC aggregate reports should reach ${deliverability.report_mailbox}.`}
      trailing={
        <Button
          color="secondary"
          size="sm"
          iconLeading={RefreshCw01}
          isLoading={checking}
          showTextWhileLoading
          onPress={recheck}
        >
          Re-check DNS
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {deliverability.domains.map((domain) => (
          <div key={domain.domain} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-primary text-sm font-semibold">
                {domain.domain}
              </span>
              <Badge color="gray" size="sm">
                {domain.role}
              </Badge>
            </div>
            {domain.available ? (
              <ul className="flex flex-col gap-2">
                <CheckRow
                  state={domain.spf!.ok}
                  label="SPF"
                  detail={domain.spf!.detail}
                />
                <CheckRow
                  state={domain.dkim!.ok}
                  label="DKIM"
                  detail={domain.dkim!.detail}
                />
                <CheckRow
                  state={domain.dmarc!.ok}
                  label="DMARC"
                  detail={domain.dmarc!.detail}
                />
              </ul>
            ) : (
              <Notice tone="warning">
                The DNS lookup for this domain failed, so the SPF, DKIM and
                DMARC checks never ran. Re-check DNS retries it.
              </Notice>
            )}
          </div>
        ))}
      </div>
    </Panel>
  )
}
